import { PublicPageView } from '../../types/enums/public-page-view.enum'

/** The `:view` segments of `/@slug/:view`. They predate the enum, so they stay as they are. */
const VIEW_SEGMENTS = new Map<string, PublicPageView>([
  ['time-table', PublicPageView.Timetable],
  ['reservation', PublicPageView.Reservations],
])

export interface PublicPageViewSettings {
  showCalendar: boolean
  showReservations: boolean
  defaultPublicView: PublicPageView
}

/**
 * Which view the public premise page opens. A view the premise has switched off is never shown,
 * whatever the URL asks for; with both on, the URL wins and the manager's default fills in when it
 * names nothing. `null` when the premise shows neither. docs/premise-settings.md has the table.
 */
export function resolvePublicPageView(entity: PublicPageViewSettings, segment?: string): PublicPageView | null {
  const { showCalendar, showReservations } = entity
  if (!showCalendar && !showReservations) return null
  if (!showReservations) return PublicPageView.Timetable
  if (!showCalendar) return PublicPageView.Reservations
  return (segment && VIEW_SEGMENTS.get(segment)) || entity.defaultPublicView
}

/** The mobile page's own tab: the premise's details, which desktop shows below the module instead. */
export const PUBLIC_INFO_TAB = 'info'

/** A tab of the mobile public page (docs/public-page.md, _Tabs and the dock_). */
export type PublicTab = typeof PUBLIC_INFO_TAB | PublicPageView

/** The URL segment of each tab, the inverse of `VIEW_SEGMENTS` plus `info`. */
const TAB_SEGMENTS: Record<PublicTab, string> = {
  [PUBLIC_INFO_TAB]: 'info',
  [PublicPageView.Timetable]: 'time-table',
  [PublicPageView.Reservations]: 'reservation',
}

export function publicTabSegment(tab: PublicTab): string {
  return TAB_SEGMENTS[tab]
}

/**
 * What a public page URL starts with: `/@demo-prevadzka`. It keeps a premise's slug out of the app's own routes, and
 * it is what nginx looks for before it asks the API for a link preview (docs/public-page.md, _Link previews_).
 */
export const PUBLIC_PAGE_PREFIX = '@'

/** The first segment of a premise's public page, `@` and its slug (or short id), for a `routerLink` array. */
export function publicPageSegment(premise: string): string {
  return `${PUBLIC_PAGE_PREFIX}${premise}`
}

/**
 * The path of a premise's public page, `/@slug` or `/@slug/:segment`. The slug is encoded, the `@` is not: a browser
 * and Angular both leave it as it is, and the URL a manager copies should read as the one they type.
 */
export function publicPagePath(premise: string, segment?: string): string {
  const path = `/${publicPageSegment(encodeURIComponent(premise))}`
  return segment ? `${path}/${encodeURIComponent(segment)}` : path
}

/**
 * The query parameter that opens one termín on the public page (docs/public-page.md, _Link to a termín_). An
 * `activityId` will join it once events are grouped by activity.
 */
export const PUBLIC_EVENT_PARAM = 'eventId'

/** The link to one termín: the timetable with `?eventId=`, which opens its sign-up step. */
export function publicEventPath(premise: string, eventId: string): string {
  const path = publicPagePath(premise, publicTabSegment(PublicPageView.Timetable))
  return `${path}?${PUBLIC_EVENT_PARAM}=${encodeURIComponent(eventId)}`
}

/**
 * The tabs of the mobile dock: Info, then the views that are on, in the desktop toggle's order. With only Info left
 * there is nowhere to switch to, so the page shows no dock (`length < 2`).
 */
export function publicTabs(entity: PublicPageViewSettings): PublicTab[] {
  return [
    PUBLIC_INFO_TAB,
    ...(entity.showCalendar ? [PublicPageView.Timetable] : []),
    ...(entity.showReservations ? [PublicPageView.Reservations] : []),
  ]
}

/**
 * Which tab the mobile page opens: `/@slug/info` opens Info, anything else follows `resolvePublicPageView`, and a
 * premise that shows neither view opens on Info. Desktop has no tabs; there `info` scrolls to the details.
 */
export function resolvePublicTab(entity: PublicPageViewSettings, segment?: string): PublicTab {
  if (segment === TAB_SEGMENTS[PUBLIC_INFO_TAB]) return PUBLIC_INFO_TAB
  return resolvePublicPageView(entity, segment) ?? PUBLIC_INFO_TAB
}

/** The document title of every page but a premise's own. Also the shell's default in `apps/web/src/index.html`. */
export const APP_TITLE = 'Seiri'

/**
 * The document title of a premise's public page: the browser tab, and the `<title>` link previews and search show
 * (docs/public-page.md, _Link previews_). The API's fragment and the app use this one function, so they agree.
 */
export function publicPageTitle(premiseName: string): string {
  const name = premiseName.trim()
  return name ? `${name} · ${APP_TITLE}` : APP_TITLE
}
